// فرم قرارداد همکاری (SCR-STAFF-800؛ US-CMP-001، 010 تا 012، 014): ایجاد با نسخه ۱ پیش‌نویس یا ویرایش پیش‌نویس؛ شعب، تاریخ‌ها، تسویه و شبا،
// کسورات ثابت، حداقل تضمینی و سقف، مبنای نقدی، دوره صورت‌وضعیت، زمان کمیسیون و کارمزد کارتخوان (AP-CMP-02)، تحمل تخفیف به ازای نوع و قواعد سهم با انتخاب خدمت از جستجوی سروری.
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CmpCommissionTiming,
  type CmpCompensationBasis,
  type CmpStatementPeriod,
  type CmpAgreementRequest,
  type CmpAgreementType,
  type CmpAgreementVersionView,
  type CmpAgreementView,
  type CmpFixedChargeKind,
  type CmpRuleScope,
  type CmpSettlementMethod,
  type CmpShareBasis,
  type CmpShareMethod,
  type CmpShareRole,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  FormDialog,
  JalaliDatePickerRHF,
  latinDigits,
  MoneyFieldRHF,
  SelectRHF,
  TextFieldRHF,
  zBusinessDate,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch, type Control } from 'react-hook-form';
import { z } from 'zod';
import { useActiveServices, useCategories } from '../catalog/common';
import {
  AGREEMENT_TYPES,
  BASES,
  BEARING_KINDS,
  CHARGE_KINDS,
  COMMISSION_TIMINGS,
  fromBasisPoints,
  parseTiers,
  STATEMENT_PERIODS,
  tiersText,
  ProviderPicker,
  RULE_BASES,
  RULE_METHODS,
  RULE_ROLES,
  RULE_SCOPES,
  SETTLEMENT_METHODS,
  toBasisPoints,
} from './common';

const percent = () =>
  z.string().refine(
    (value) => {
      const number = Number(latinDigits(value.trim()));
      return value.trim() !== '' && Number.isFinite(number) && number >= 0 && number <= 100;
    },
    { error: 'common:validation.invalid' },
  );

const ruleSchema = z
  .object({
    role: z.enum(RULE_ROLES as [CmpShareRole, ...CmpShareRole[]]),
    scope: z.enum(RULE_SCOPES as [CmpRuleScope, ...CmpRuleScope[]]),
    scopeId: z.string().nullable(),
    basis: z.enum(RULE_BASES as [CmpShareBasis, ...CmpShareBasis[]]),
    method: z.enum(RULE_METHODS as [CmpShareMethod, ...CmpShareMethod[]]),
    rate: z.string(),
    amount: z.number().nullable(),
    tiers: z.string(),
  })
  .superRefine((rule, context) => {
    if (rule.scope !== 'All' && !rule.scopeId) {
      context.addIssue({ code: 'custom', path: ['scopeId'], message: 'common:validation.required' });
    }
    if (rule.method === 'Tiered') {
      if (parseTiers(rule.tiers) === null) {
        context.addIssue({ code: 'custom', path: ['tiers'], message: 'common:validation.invalid' });
      }
    } else if (rule.method === 'Percent') {
      const number = Number(latinDigits(rule.rate.trim()));
      if (rule.rate.trim() === '' || !Number.isFinite(number) || number < 0 || number > 100) {
        context.addIssue({ code: 'custom', path: ['rate'], message: 'common:validation.invalid' });
      }
    } else if (rule.amount === null) {
      context.addIssue({ code: 'custom', path: ['amount'], message: 'common:validation.required' });
    }
  });

const agreementSchema = z.object({
  providerId: z
    .string()
    .nullable()
    .refine((value) => Boolean(value), { error: 'common:validation.required' }),
  title: z.string().max(200, { error: 'common:validation.too_long' }),
  type: z.enum(AGREEMENT_TYPES as [CmpAgreementType, ...CmpAgreementType[]]),
  basis: z.enum(BASES as [CmpCompensationBasis, ...CmpCompensationBasis[]]),
  statementPeriod: z.enum(STATEMENT_PERIODS as [CmpStatementPeriod, ...CmpStatementPeriod[]]),
  commissionTiming: z.enum(COMMISSION_TIMINGS as [CmpCommissionTiming, ...CmpCommissionTiming[]]),
  cardFee: percent(),
  shareCap: z.number().nullable(),
  branchIds: z.array(z.string()),
  effectiveFrom: zBusinessDate(),
  effectiveTo: z.string().nullable(),
  settlementMethod: z.string(),
  iban: z.string(),
  minimumGuarantee: z.number().nullable(),
  note: z.string().max(1000, { error: 'common:validation.too_long' }),
  bearing: z.object({
    automatic: percent(),
    coupon: percent(),
    manualBySelf: percent(),
    manualByOthers: percent(),
    rounding: percent(),
  }),
  charges: z.array(
    z.object({
      kind: z.enum(CHARGE_KINDS as [CmpFixedChargeKind, ...CmpFixedChargeKind[]]),
      amount: z.number().nullable(),
      title: z.string().max(120),
    }),
  ),
  rules: z.array(ruleSchema),
});
type AgreementForm = z.input<typeof agreementSchema>;

const DEFAULT_BEARING = {
  automatic: '50',
  coupon: '50',
  manualBySelf: '100',
  manualByOthers: '0',
  rounding: '0',
};

function formOf(
  agreement: CmpAgreementView | undefined,
  version: CmpAgreementVersionView | undefined,
): AgreementForm {
  const asText = (value: number | string | null | undefined) => String(fromBasisPoints(value) ?? 0);
  return {
    providerId: agreement?.providerId ?? null,
    title: agreement?.title ?? '',
    type: version?.type ?? 'FeeForService',
    basis: version?.basis ?? 'Accrual',
    statementPeriod: version?.statementPeriod ?? 'Monthly',
    commissionTiming: version?.commissionTiming ?? 'PerSession',
    cardFee: asText(version?.cardFeeBasisPoints ?? 0),
    shareCap: version?.shareCap ? Number(version.shareCap.amount) : null,
    branchIds: [...(version?.branchIds ?? [])],
    effectiveFrom: version?.effectiveFrom ?? '',
    effectiveTo: version?.effectiveTo ?? null,
    settlementMethod: version?.settlementMethod ?? '',
    iban: version?.iban ?? '',
    minimumGuarantee: version?.minimumGuarantee ? Number(version.minimumGuarantee.amount) : null,
    note: version?.note ?? '',
    bearing: version
      ? {
          automatic: asText(version.discountBearing.automatic),
          coupon: asText(version.discountBearing.coupon),
          manualBySelf: asText(version.discountBearing.manualBySelf),
          manualByOthers: asText(version.discountBearing.manualByOthers),
          rounding: asText(version.discountBearing.rounding),
        }
      : { ...DEFAULT_BEARING },
    charges: (version?.fixedCharges ?? []).map((charge) => ({
      kind: charge.kind,
      amount: Number(charge.amount.amount),
      title: charge.title ?? '',
    })),
    rules: (version?.rules ?? []).map((rule) => ({
      role: rule.role,
      scope: rule.scope,
      scopeId: rule.scopeId ?? null,
      basis: rule.basis,
      method: rule.method,
      rate: String(fromBasisPoints(rule.rateBasisPoints) ?? ''),
      amount: rule.amount ? Number(rule.amount.amount) : null,
      tiers: tiersText(rule.tiers ?? []),
    })),
  };
}

function toRequest(form: AgreementForm, rowVersion?: number | string): CmpAgreementRequest {
  const rials = (amount: number) => ({ amount, currency: 'IRR' });
  const bp = (value: string) => toBasisPoints(Number(latinDigits(value.trim())));
  return {
    providerId: form.providerId ?? '',
    title: form.title.trim() || null,
    type: form.type,
    basis: form.basis,
    statementPeriod: form.statementPeriod,
    commissionTiming: form.commissionTiming,
    cardFeeBasisPoints: bp(form.cardFee),
    shareCap: form.shareCap === null ? undefined : rials(form.shareCap),
    branchIds: form.branchIds,
    effectiveFrom: form.effectiveFrom,
    effectiveTo: form.effectiveTo || null,
    settlementMethod: (form.settlementMethod || null) as CmpSettlementMethod | null,
    iban: latinDigits(form.iban.replace(/\s/g, '')) || null,
    minimumGuarantee: form.minimumGuarantee === null ? null : rials(form.minimumGuarantee),
    discountBearing: {
      automatic: bp(form.bearing.automatic),
      coupon: bp(form.bearing.coupon),
      manualBySelf: bp(form.bearing.manualBySelf),
      manualByOthers: bp(form.bearing.manualByOthers),
      rounding: bp(form.bearing.rounding),
    },
    fixedCharges: form.charges
      .filter((charge) => charge.amount !== null)
      .map((charge) => ({
        kind: charge.kind,
        amount: rials(charge.amount ?? 0),
        title: charge.title.trim() || null,
      })),
    rules: form.rules.map((rule) => ({
      role: rule.role,
      scope: rule.scope,
      scopeId: rule.scope === 'All' ? null : rule.scopeId,
      basis: rule.basis,
      method: rule.method,
      rateBasisPoints:
        rule.method === 'Percent' ? toBasisPoints(Number(latinDigits(rule.rate.trim()))) : null,
      amount: rule.method === 'Percent' || rule.method === 'Tiered' ? null : rials(rule.amount ?? 0),
      tiers:
        rule.method === 'Tiered'
          ? (parseTiers(rule.tiers) ?? []).map((tier) => ({
              from: rials(tier.from),
              rateBasisPoints: tier.rateBasisPoints,
            }))
          : null,
    })),
    note: form.note.trim() || null,
    ...(rowVersion === undefined ? {} : { rowVersion: Number(rowVersion) }),
  };
}

/** ایجاد قرارداد (بدون <paramref name="agreement"/>) یا ویرایش نسخه پیش‌نویس. */
export function AgreementDialog({
  agreement,
  version,
  onClose,
}: {
  agreement?: CmpAgreementView;
  version?: CmpAgreementVersionView;
  onClose: (saved?: CmpAgreementView) => void;
}) {
  const { t } = useTranslation('cmp');
  const { tenantApi, activeTenant } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = agreement === undefined;
  const branches = activeTenant?.branches ?? [];
  const { control, handleSubmit, formState, setError } = useForm<AgreementForm>({
    resolver: zodResolver(agreementSchema),
    defaultValues: formOf(agreement, version),
  });
  const rules = useFieldArray({ control, name: 'rules' });
  const charges = useFieldArray({ control, name: 'charges' });

  const save = useMutation({
    mutationFn: (form: AgreementForm) =>
      unwrap(
        creating || !version
          ? tenantApi.POST('/api/v1/compensation/agreements', {
              body: toRequest(form),
              headers: { 'Idempotency-Key': idempotency.current() },
            })
          : tenantApi.PUT('/api/v1/compensation/agreements/{id}/versions/{versionId}', {
              params: { path: { id: agreement.id, versionId: version.id } },
              body: toRequest(form, agreement.rowVersion),
            }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: (saved) => onClose(saved),
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: [
          'providerId',
          'title',
          'type',
          'effectiveFrom',
          'effectiveTo',
          'iban',
          'minimumGuarantee',
          'note',
          'rules',
        ],
        codeFields: {
          'PHX-CMP-0106': 'providerId',
          'PHX-CMP-0103': 'rules',
          'PHX-CMP-0203': 'effectiveFrom',
        },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      maxWidth="md"
      screenId="SCR-STAFF-800-AGREEMENT"
      titleKey={creating ? 'cmp:form.create_title' : 'cmp:form.edit_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          control={control}
          name="providerId"
          render={({ field, fieldState }) => (
            <ProviderPicker
              value={field.value ?? null}
              onChange={field.onChange}
              labelKey="cmp:form.provider"
              testId="agreement-provider"
              disabled={!creating}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
        <TextFieldRHF
          control={control}
          name="title"
          labelKey="cmp:form.title"
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'agreement-title' } }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="type"
          labelKey="cmp:form.type"
          required
          fullWidth
          options={AGREEMENT_TYPES.map((value) => ({ value, labelKey: `cmp:agreement_type.${value}` }))}
        />
        <SelectRHF
          control={control}
          name="basis"
          labelKey="cmp:form.basis"
          required
          fullWidth
          options={BASES.map((value) => ({ value, labelKey: `cmp:basis.${value}` }))}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="statementPeriod"
          labelKey="cmp:form.statement_period"
          fullWidth
          options={STATEMENT_PERIODS.map((value) => ({ value, labelKey: `cmp:statement_period.${value}` }))}
        />
        <SelectRHF
          control={control}
          name="commissionTiming"
          labelKey="cmp:form.commission_timing"
          fullWidth
          options={COMMISSION_TIMINGS.map((value) => ({ value, labelKey: `cmp:commission_timing.${value}` }))}
        />
        <TextFieldRHF
          control={control}
          name="cardFee"
          labelKey="cmp:form.card_fee"
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': 'agreement-card-fee' } }}
        />
      </Stack>
      <Controller
        control={control}
        name="branchIds"
        render={({ field }) => (
          <Autocomplete
            multiple
            options={branches.map((branch) => branch.branchId)}
            value={field.value}
            onChange={(_, value) => field.onChange(value)}
            getOptionLabel={(id) => branches.find((branch) => branch.branchId === id)?.name ?? id.slice(0, 8)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('cmp:form.branches')}
                helperText={t('cmp:form.branches_help')}
              />
            )}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF
          control={control}
          name="effectiveFrom"
          labelKey="cmp:form.effective_from"
          required
        />
        <JalaliDatePickerRHF control={control} name="effectiveTo" labelKey="cmp:form.effective_to" />
      </Stack>

      <Typography variant="subtitle2">{t('cmp:form.rules_title')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('cmp:form.rules_help')}
      </Typography>
      {rules.fields.length === 0 && <Typography variant="body2">{t('cmp:form.rules_empty')}</Typography>}
      {rules.fields.map((field, index) => (
        <RuleRow key={field.id} control={control} index={index} onRemove={() => rules.remove(index)} />
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={() =>
          rules.append({
            role: 'Primary',
            scope: 'All',
            scopeId: null,
            basis: 'NetOfDiscount',
            method: 'Percent',
            rate: '',
            amount: null,
            tiers: '',
          })
        }
        data-testid="agreement-add-rule"
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('cmp:form.add_rule')}
      </Button>

      <Typography variant="subtitle2">{t('cmp:form.bearing_title')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('cmp:form.bearing_help')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {BEARING_KINDS.map((kind) => (
          <TextFieldRHF
            key={kind}
            control={control}
            name={`bearing.${kind}`}
            labelKey={`cmp:discount_kind.${kind}`}
            fullWidth
            slotProps={{
              htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': `agreement-bearing-${kind}` },
            }}
          />
        ))}
      </Stack>

      <Typography variant="subtitle2">{t('cmp:form.charges_title')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('cmp:form.charges_help')}
      </Typography>
      {charges.fields.map((field, index) => (
        <Stack key={field.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <SelectRHF
            control={control}
            name={`charges.${index}.kind`}
            labelKey="cmp:form.charge_kind"
            fullWidth
            options={CHARGE_KINDS.map((value) => ({ value, labelKey: `cmp:charge_kind.${value}` }))}
          />
          <MoneyFieldRHF
            control={control}
            name={`charges.${index}.amount`}
            labelKey="cmp:form.charge_amount"
            fullWidth
          />
          <TextFieldRHF
            control={control}
            name={`charges.${index}.title`}
            labelKey="cmp:form.charge_title"
            fullWidth
          />
          <IconButton aria-label={t('cmp:form.remove')} onClick={() => charges.remove(index)}>
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={() => charges.append({ kind: 'Rent', amount: null, title: '' })}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('cmp:form.add_charge')}
      </Button>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <MoneyFieldRHF
          control={control}
          name="minimumGuarantee"
          labelKey="cmp:form.minimum_guarantee"
          fullWidth
        />
        <MoneyFieldRHF control={control} name="shareCap" labelKey="cmp:form.share_cap" fullWidth />
        <SelectRHF
          control={control}
          name="settlementMethod"
          labelKey="cmp:form.settlement_method"
          fullWidth
          options={[
            { value: '', labelKey: 'cmp:form.none' },
            ...SETTLEMENT_METHODS.map((value) => ({
              value: String(value),
              labelKey: `cmp:settlement.${String(value)}`,
            })),
          ]}
        />
        <TextFieldRHF
          control={control}
          name="iban"
          labelKey="cmp:form.iban"
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'agreement-iban' } }}
        />
      </Stack>
      <TextFieldRHF control={control} name="note" labelKey="cmp:form.note" fullWidth multiline minRows={2} />
    </FormDialog>
  );
}

/** یک قاعده سهم: نقش، دامنه (خدمت با جستجوی سروری یا دسته)، مبنا، روش و نرخ یا مبلغ. */
function RuleRow({
  control,
  index,
  onRemove,
}: {
  control: Control<AgreementForm>;
  index: number;
  onRemove: () => void;
}) {
  const { t } = useTranslation('cmp');
  const services = useActiveServices();
  const categories = useCategories();
  const scope = useWatch({ control, name: `rules.${index}.scope` });
  const method = useWatch({ control, name: `rules.${index}.method` });

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems="flex-start"
      data-testid={`agreement-rule-${index}`}
    >
      <SelectRHF
        control={control}
        name={`rules.${index}.role`}
        labelKey="cmp:form.rule_role"
        fullWidth
        options={RULE_ROLES.map((value) => ({ value, labelKey: `cmp:role.${value}` }))}
      />
      <SelectRHF
        control={control}
        name={`rules.${index}.scope`}
        labelKey="cmp:form.rule_scope"
        fullWidth
        options={RULE_SCOPES.map((value) => ({ value, labelKey: `cmp:scope.${value}` }))}
      />
      {scope === 'Service' && (
        <Controller
          control={control}
          name={`rules.${index}.scopeId`}
          render={({ field, fieldState }) => (
            <Autocomplete
              fullWidth
              options={services.options.map((option) => option.id)}
              value={field.value}
              onChange={(_, value) => field.onChange(value)}
              getOptionLabel={(id) => services.name(id)}
              filterOptions={(options) => options}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  services.search(value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('cmp:form.rule_service')}
                  placeholder={t('cmp:form.rule_service_search')}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  slotProps={{
                    htmlInput: { ...params.inputProps, 'data-testid': `agreement-rule-service-${index}` },
                  }}
                />
              )}
            />
          )}
        />
      )}
      {scope === 'Category' && (
        <SelectRHF
          control={control}
          name={`rules.${index}.scopeId`}
          labelKey="cmp:form.rule_category"
          fullWidth
          options={(categories.data?.items ?? []).map((category) => ({
            value: category.id,
            label: category.name,
          }))}
        />
      )}
      <SelectRHF
        control={control}
        name={`rules.${index}.basis`}
        labelKey="cmp:form.rule_basis"
        fullWidth
        options={RULE_BASES.map((value) => ({ value, labelKey: `cmp:share_basis.${value}` }))}
      />
      <SelectRHF
        control={control}
        name={`rules.${index}.method`}
        labelKey="cmp:form.rule_method"
        fullWidth
        options={RULE_METHODS.map((value) => ({ value, labelKey: `cmp:method.${value}` }))}
      />
      {method === 'Tiered' ? (
        <TextFieldRHF
          control={control}
          name={`rules.${index}.tiers`}
          labelKey="cmp:form.rule_tiers"
          helperKey="cmp:form.rule_tiers_help"
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `agreement-rule-tiers-${index}` } }}
        />
      ) : method === 'Percent' ? (
        <TextFieldRHF
          control={control}
          name={`rules.${index}.rate`}
          labelKey="cmp:form.rule_rate"
          fullWidth
          slotProps={{
            htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': `agreement-rule-rate-${index}` },
          }}
        />
      ) : (
        <MoneyFieldRHF
          control={control}
          name={`rules.${index}.amount`}
          labelKey="cmp:form.rule_amount"
          fullWidth
        />
      )}
      <IconButton aria-label={t('cmp:form.remove')} onClick={onRemove}>
        <DeleteOutlineIcon />
      </IconButton>
    </Stack>
  );
}
